// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { toProjectStatus } from '@/constants/project-status';
import {
  type FulfillmentBySkuReportFilter,
  fulfillmentBySkuReportRowSchema,
} from '@/report/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import queryString from 'query-string';
import { z } from 'zod';

const fulfillmentBySkuReportResponseSchema = z
  .object({
    data: z.object({
      results: z.array(fulfillmentBySkuReportRowSchema),
      totalCount: z.number(),
    }),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    rows: data.results,
    totalCount: data.totalCount,
  }));

type FulfillmentBySkuReportResponse = z.infer<
  typeof fulfillmentBySkuReportResponseSchema
>;

export function serializeFulfillmentBySkuReportFilter(
  filter?: FulfillmentBySkuReportFilter
): string {
  if (!filter) {
    return '';
  }

  return queryString.stringify({
    statuses: filter.statuses,
    projectIds: filter.projectIds,
    projectStatuses: filter.projectStatuses,
    companyTypes: filter.companyTypes,
    companyIds: filter.companyIds,
    itemIds: filter.itemIds,
    needByFrom: serializeDateForAPI(filter.needByFrom),
    needByTo: serializeDateForAPI(filter.needByTo),
    limit: filter.pageSize,
    page: filter.pageNumber,
    sortBy: filter.sort?.property,
    sortOrder: filter.sort?.direction ?? 'asc',
  });
}

export async function getFulfillmentBySkuReport(
  filter?: FulfillmentBySkuReportFilter
): Promise<FulfillmentBySkuReportResponse> {
  const url = '/v1/report/fulfillment/by-sku';
  const response = await api.get(url, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: {
      statuses: filter?.statuses,
      projectIds: filter?.projectIds,
      projectStatuses: filter?.projectStatuses?.map(toProjectStatus),
      companyTypes: filter?.companyTypes,
      companyIds: filter?.companyIds,
      itemIds: filter?.itemIds,
      needByFrom: serializeDateForAPI(filter?.needByFrom),
      needByTo: serializeDateForAPI(filter?.needByTo),
      limit: filter?.pageSize,
      page: filter?.pageNumber,
      sortBy: filter?.sort?.property,
      sortOrder: filter?.sort?.direction ?? 'asc',
    },
  });

  return parseResponse(fulfillmentBySkuReportResponseSchema, response.data, {
    schemaName: 'FulfillmentBySkuReport',
    endpoint: url,
  });
}
